import { MODEL_DEFINITIONS } from '../../config/models';
import { normalizeModelRepository } from '../../config/models/schema';
import {
  buildTimingAliasMap,
  lookupTimingProfileForKey,
} from '../../config/models/timingAliases';
import {
  installDynamicModelEnhancementResolver,
  setRepositoryLookupForEnhancement,
} from './modelEnhancementResolver';

function buildSnapshot(models) {
  const byId = new Map(models.map((model) => [model.id, model]));
  const idSet = new Set(byId.keys());
  const timingAliasByKey = buildTimingAliasMap(models);
  return { models, byId, idSet, timingAliasByKey };
}

const DEFAULT_BUILTIN_REPOSITORY = normalizeModelRepository(MODEL_DEFINITIONS);
const DEFAULT_SNAPSHOT = buildSnapshot(DEFAULT_BUILTIN_REPOSITORY);

let currentSnapshot = DEFAULT_SNAPSHOT;
let version = 0;
const subscribers = new Set();

function notifySubscribers() {
  version += 1;
  for (const subscriber of [...subscribers]) {
    try {
      subscriber();
    } catch (error) {
      console.error('[modelRepositoryRuntime] subscriber threw', error);
    }
  }
}

export function buildEffectiveModelRepository(catalogDefinitions = []) {
  const defs = Array.isArray(catalogDefinitions) ? catalogDefinitions : [];
  if (defs.length === 0) return DEFAULT_BUILTIN_REPOSITORY;
  return normalizeModelRepository([...MODEL_DEFINITIONS, ...defs]);
}

export function setCurrentModelRepository(repository) {
  if (!Array.isArray(repository)) {
    throw new Error('[modelRepositoryRuntime] setCurrentModelRepository expects an array.');
  }
  currentSnapshot = buildSnapshot(repository);
  notifySubscribers();
}

export function resetModelRepository() {
  if (currentSnapshot === DEFAULT_SNAPSHOT) return;
  currentSnapshot = DEFAULT_SNAPSHOT;
  notifySubscribers();
}

export function getCurrentModelRepository() {
  return currentSnapshot.models;
}

export function getCurrentModelById(modelId) {
  if (typeof modelId !== 'string') return null;
  const normalized = modelId.trim();
  if (!normalized) return null;
  return currentSnapshot.byId.get(normalized) || null;
}

export function getCurrentModelIdSet() {
  return currentSnapshot.idSet;
}

export function getCurrentTimingProfileForKey(modelKey) {
  return lookupTimingProfileForKey({
    modelByIdMap: currentSnapshot.byId,
    timingAliasByKey: currentSnapshot.timingAliasByKey,
    modelKey,
  });
}

export function getModelRepositoryVersion() {
  return version;
}

export function subscribeModelRepository(subscriber) {
  if (typeof subscriber !== 'function') {
    return () => {};
  }
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}

setRepositoryLookupForEnhancement(getCurrentModelById);
installDynamicModelEnhancementResolver();
